import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface QuestionnaireProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const Questionnaire = ({ open, onOpenChange }: QuestionnaireProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState({
    commitment: "",
    goals: "",
    experience: "",
  });

  const handleNext = () => {
    if (step < 3) {
      setStep(step + 1);
    } else {
      // Determine which plan to recommend
      let recommendation = "/categories/normal-people";
      if (answers.goals === "individual") {
        recommendation = "/view-plans";
      } else if (answers.goals === "performance") {
        recommendation = "/categories/elite-athletes";
      } else if (answers.goals === "eliteFitness") {
        recommendation = "/categories/elite-athletes";
      } else if (answers.goals === "football") {
        recommendation = "/categories/football-training";
      } else if (answers.goals === "athletic") {
        recommendation = "/categories/athletic-training";
      }
      navigate(recommendation);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border">
        <DialogHeader>
          <DialogTitle className="text-foreground text-2xl">{t("questionnaire.findPerfectPlan")}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {step === 1 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground">{t("questionnaire.commitmentLevel")}</h3>
              <RadioGroup value={answers.commitment} onValueChange={(val) => setAnswers({...answers, commitment: val})}>
                <div className="flex items-center space-x-2 p-3 rounded-md hover:bg-muted cursor-pointer">
                  <RadioGroupItem value="fully" id="fully" />
                  <Label htmlFor="fully" className="cursor-pointer text-foreground">{t("questionnaire.fullyCommitted")}</Label>
                </div>
                <div className="flex items-center space-x-2 p-3 rounded-md hover:bg-muted cursor-pointer">
                  <RadioGroupItem value="balanced" id="balanced" />
                  <Label htmlFor="balanced" className="cursor-pointer text-foreground">{t("questionnaire.balanced")}</Label>
                </div>
              </RadioGroup>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground">{t("questionnaire.mainGoals")}</h3>
              <RadioGroup value={answers.goals} onValueChange={(val) => setAnswers({...answers, goals: val})}>
                <div className="flex items-center space-x-2 p-3 rounded-md hover:bg-muted cursor-pointer">
                  <RadioGroupItem value="fitness" id="fitness" />
                  <Label htmlFor="fitness" className="cursor-pointer text-foreground">{t("questionnaire.generalFitness")}</Label>
                </div>
                <div className="flex items-center space-x-2 p-3 rounded-md hover:bg-muted cursor-pointer">
                  <RadioGroupItem value="eliteFitness" id="eliteFitness" />
                  <Label htmlFor="eliteFitness" className="cursor-pointer text-foreground">{t("questionnaire.eliteFitness")}</Label>
                </div>
                <div className="flex items-center space-x-2 p-3 rounded-md hover:bg-muted cursor-pointer">
                  <RadioGroupItem value="athletic" id="athletic" />
                  <Label htmlFor="athletic" className="cursor-pointer text-foreground">Athletic Training (speed, power, conditioning)</Label>
                </div>
                <div className="flex items-center space-x-2 p-3 rounded-md hover:bg-muted cursor-pointer">
                  <RadioGroupItem value="football" id="football" />
                  <Label htmlFor="football" className="cursor-pointer text-foreground">{t("questionnaire.footballSpecific")}</Label>
                </div>
                <div className="flex items-center space-x-2 p-3 rounded-md hover:bg-muted cursor-pointer">
                  <RadioGroupItem value="performance" id="performance" />
                  <Label htmlFor="performance" className="cursor-pointer text-foreground">{t("questionnaire.elitePerformance")}</Label>
                </div>
                <div className="flex items-center space-x-2 p-3 rounded-md hover:bg-muted cursor-pointer">
                  <RadioGroupItem value="individual" id="individual" />
                  <Label htmlFor="individual" className="cursor-pointer text-foreground">{t("questionnaire.trySessions")}</Label>
                </div>
              </RadioGroup>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground">{t("questionnaire.trainingExperience")}</h3>
              <RadioGroup value={answers.experience} onValueChange={(val) => setAnswers({...answers, experience: val})}>
                <div className="flex items-center space-x-2 p-3 rounded-md hover:bg-muted cursor-pointer">
                  <RadioGroupItem value="beginner" id="beginner" />
                  <Label htmlFor="beginner" className="cursor-pointer text-foreground">{t("questionnaire.beginner")}</Label>
                </div>
                <div className="flex items-center space-x-2 p-3 rounded-md hover:bg-muted cursor-pointer">
                  <RadioGroupItem value="intermediate" id="intermediate" />
                  <Label htmlFor="intermediate" className="cursor-pointer text-foreground">{t("questionnaire.intermediate")}</Label>
                </div>
                <div className="flex items-center space-x-2 p-3 rounded-md hover:bg-muted cursor-pointer">
                  <RadioGroupItem value="advanced" id="advanced" />
                  <Label htmlFor="advanced" className="cursor-pointer text-foreground">{t("questionnaire.advanced")}</Label>
                </div>
              </RadioGroup>
            </div>
          )}

          <div className="flex justify-between pt-4">
            {step > 1 && (
              <Button variant="outline" onClick={() => setStep(step - 1)}>
                {t("questionnaire.back")}
              </Button>
            )}
            <Button 
              onClick={handleNext} 
              className="ml-auto shadow-accent-glow"
              disabled={
                (step === 1 && !answers.commitment) ||
                (step === 2 && !answers.goals) ||
                (step === 3 && !answers.experience)
              }
            >
              {step === 3 ? t("questionnaire.seeRecommendation") : t("questionnaire.next")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
